import { apiService } from './api';

// Get the same base URL that apiService uses
let API_BASE = import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? '' : '/api');
if (API_BASE.endsWith('/api')) {
  API_BASE = API_BASE.slice(0, -4);
}

export interface Backup {
  filename: string;
  size: number;
  created: string;
  modified: string;
}

export interface BackupListResponse {
  backups: Backup[];
  totalCount: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export interface RestoreResponse {
  success: boolean;
  message: string;
  filename: string;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
  filename: string;
}

export interface CreateBackupResponse {
  success: boolean;
  message: string;
  output?: string;
}

export interface BackupScheduleResponse {
  schedule: string;
}

export interface UpdateBackupScheduleResponse {
  success: boolean;
  message: string;
  schedule: string;
}

class BackupService {
  private baseUrl = '/api/backups';

  /**
   * Get list of all backup files
   */
  async getBackups(params?: { page?: number; pageSize?: number; search?: string }): Promise<BackupListResponse> {
    const sp = new URLSearchParams();
    if (params?.page) sp.append('page', String(params.page));
    if (params?.pageSize) sp.append('pageSize', String(params.pageSize));
    if (params?.search && String(params.search).trim()) sp.append('search', String(params.search).trim());
    const qs = sp.toString();
    const endpoint = qs ? `${this.baseUrl}?${qs}` : this.baseUrl;
    return await apiService.get<BackupListResponse>(endpoint);
  }

  /**
   * Restore database from a backup file
   */
  async restoreBackup(filename: string): Promise<RestoreResponse> {
    return await apiService.post<RestoreResponse>(
      `${this.baseUrl}/${encodeURIComponent(filename)}/restore`
    );
  }

  /**
   * Delete a backup file
   */
  async deleteBackup(filename: string): Promise<DeleteResponse> {
    return await apiService.delete<DeleteResponse>(
      `${this.baseUrl}/${encodeURIComponent(filename)}`
    );
  }

  /**
   * Download a backup file
   */
  async downloadBackup(filename: string): Promise<Blob> {
    const headers = this.getAuthHeaders({ includeContentType: false });
    const response = await fetch(`${API_BASE}${this.baseUrl}/${encodeURIComponent(filename)}/download`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      let message = response.statusText || 'Bad Request';
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          message = data?.error || data?.message || message;
        } else {
          const text = await response.text();
          if (text && text.trim()) message = text;
        }
      } catch {
        // ignore parse errors
      }
      throw new Error(`Failed to download backup: ${message}`);
    }

    return await response.blob();
  }

  /**
   * Create a manual backup immediately
   */
  async createBackupNow(): Promise<CreateBackupResponse> {
    return await apiService.post<CreateBackupResponse>(`${this.baseUrl}/create`, {});
  }

  /**
   * Get current backup cron schedule
   */
  async getSchedule(): Promise<BackupScheduleResponse> {
    return await apiService.get<BackupScheduleResponse>(`${this.baseUrl}/schedule`);
  }

  /**
   * Update backup cron schedule
   */
  async updateSchedule(schedule: string): Promise<UpdateBackupScheduleResponse> {
    return await apiService.put<UpdateBackupScheduleResponse>(`${this.baseUrl}/schedule`, { schedule });
  }

  /**
   * Upload a backup file from external source
   */
  async uploadBackup(file: File): Promise<{ success: boolean; message: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const headers = this.getAuthHeaders();
    // Remove Content-Type to let browser set it with boundary for FormData
    delete (headers as any)['Content-Type'];

    const response = await fetch(`${API_BASE}${this.baseUrl}/upload`, {
      method: 'POST',
      headers: headers as any,
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload thất bại');
    }

    return await response.json();
  }

  private getAuthHeaders(opts?: { includeContentType?: boolean }): HeadersInit {
    const headers: HeadersInit = {};
    if (opts?.includeContentType !== false) {
      headers['Content-Type'] = 'application/json';
    }

    // user id: prefer sessionStorage (source of truth in this app)
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.id) {
          headers['x-user-id'] = user.id;
        }
      }
    } catch {
      // ignore
    }

    // tenant header required by backend middleware
    try {
      const schoolCode = localStorage.getItem('schoolCode');
      if (schoolCode && String(schoolCode).trim()) {
        headers['x-school-code'] = String(schoolCode).trim();
      }
    } catch {
      // ignore
    }

    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return headers;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

export const backupService = new BackupService();

